import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProjectCard from '../components/ProjectCard';
import PageSEO from '../components/PageSEO';
import { getFeaturedProjects, ProjectWithDetails } from '../lib/api/projects';
import { hasProjectAccess } from '../lib/api/projectAccess';
import {
  Search as SearchIcon,
  Work as WorkIcon,
  Palette as DesignIcon,
  Psychology as PsychologyIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  FormatQuote as QuoteIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

const getProcessSteps = (t: any) => [
  {
    step: 1,
    title: t('process.steps.step1.title'),
    description: t('process.steps.step1.description'),
    icon: <EditIcon />,
    gradient: 'linear-gradient(135deg, #20B2AA 0%, #87CEEB 100%)', // Turquoise gradient
  },
  {
    step: 2,
    title: t('process.steps.step2.title'),
    description: t('process.steps.step2.description'),
    icon: <SearchIcon />,
    gradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', // Blue gradient
  },
  {
    step: 3,
    title: t('process.steps.step3.title'),
    description: t('process.steps.step3.description'),
    icon: <AttachMoneyIcon />,
    gradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', // Blue gradient
  },
  {
    step: 4,
    title: t('process.steps.step4.title'),
    description: t('process.steps.step4.description'),
    icon: <CheckCircleOutlineIcon />,
    gradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', // Blue gradient
  },
];

const getCategories = (t: any) => [
  { 
    key: 'marketing',
    title: t('categories.items.marketing.title'),
    subtitle: t('categories.items.marketing.subtitle'),
    services: t('categories.items.marketing.services'),
    bulletPoints: t('categories.items.marketing.bulletPoints'),
    icon: <TrendingUpIcon />, 
    color: "#f59e0b" 
  },
  { 
    key: 'design',
    title: t('categories.items.design.title'),
    subtitle: t('categories.items.design.subtitle'),
    services: t('categories.items.design.services'),
    bulletPoints: t('categories.items.design.bulletPoints'),
    icon: <DesignIcon />, 
    color: "#8b5cf6" 
  },
  { 
    key: 'programming',
    title: t('categories.items.programming.title'),
    subtitle: t('categories.items.programming.subtitle'),
    services: t('categories.items.programming.services'),
    bulletPoints: t('categories.items.programming.bulletPoints'),
    icon: <WorkIcon />, 
    color: "#3b82f6" 
  },
  { 
    key: 'it',
    title: t('categories.items.it.title'),
    subtitle: t('categories.items.it.subtitle'),
    services: t('categories.items.it.services'),
    bulletPoints: t('categories.items.it.bulletPoints'),
    icon: <PsychologyIcon />, 
    color: "#28A0F0" 
  },
  { 
    key: 'audiovisual',
    title: t('categories.items.audiovisual.title'),
    subtitle: t('categories.items.audiovisual.subtitle'),
    services: t('categories.items.audiovisual.services'),
    bulletPoints: t('categories.items.audiovisual.bulletPoints'),
    icon: <PlayIcon />, 
    color: "#ec4899" 
  },
  { 
    key: 'business',
    title: t('categories.items.business.title'),
    subtitle: t('categories.items.business.subtitle'),
    services: t('categories.items.business.services'),
    bulletPoints: t('categories.items.business.bulletPoints'),
    icon: <GroupsIcon />, 
    color: "#10b981" 
  },
];


export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const categories = getCategories(t);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragCurrent, setDragCurrent] = useState(0);
  const processSteps = getProcessSteps(t);
  
  
  // State for real projects
  const [featuredProjects, setFeaturedProjects] = useState<ProjectWithDetails[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [projectAccess, setProjectAccess] = useState<Set<string>>(new Set());

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setDragStart(clientX);
    setDragCurrent(clientX);
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setDragCurrent(clientX);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    const dragDistance = dragCurrent - dragStart;
    const threshold = 50; // Minimum distance to trigger slide
    const totalTestimonials = (t('testimonials.testimonials', { returnObjects: true }) as any[]).length;
    
    if (Math.abs(dragDistance) > threshold) {
      if (dragDistance > 0) {
        // Drag right - go to previous (infinite)
        setCurrentTestimonial(prev => prev === 0 ? totalTestimonials - 1 : prev - 1);
      } else {
        // Drag left - go to next (infinite)
        setCurrentTestimonial(prev => prev === totalTestimonials - 1 ? 0 : prev + 1);
      }
    }
    
    setDragStart(0);
    setDragCurrent(0);
  };

  const goToNext = () => {
    const totalTestimonials = (t('testimonials.testimonials', { returnObjects: true }) as any[]).length;
    setCurrentTestimonial(prev => prev === totalTestimonials - 1 ? 0 : prev + 1);
  };

  const goToPrevious = () => {
    const totalTestimonials = (t('testimonials.testimonials', { returnObjects: true }) as any[]).length;
    setCurrentTestimonial(prev => prev === 0 ? totalTestimonials - 1 : prev - 1);
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleRegister = () => {
    navigate('/register');
  };


  const handleFindTalent = () => {
    navigate('/find-talent');
  };

  const handleFindWork = () => {
    navigate('/find-work');
  };

  // Load featured projects on component mount
  useEffect(() => {
    const loadFeaturedProjects = async () => {
      try {
        setProjectsLoading(true);
        setProjectsError(null);
        
        console.log('Loading featured projects...');
        
        // First try to get featured projects
        let projects = await getFeaturedProjects(2);
        console.log('Featured projects:', projects);
        
        // If no featured projects, get the latest 2 projects
        if (projects.length === 0) {
          console.log('No featured projects, getting latest projects...');
          const { getProjects } = await import('../lib/api/projects');
          const result = await getProjects({
            search: '',
            category_id: '',
            budget_min: undefined,
            budget_max: undefined,
            skills: [],
            featured: false,
            sort_by: 'created_at',
            sort_order: 'desc',
            page: 1,
            limit: 2
          });
          projects = result.projects;
          console.log('Latest projects:', projects);
        }
        
        setFeaturedProjects(projects);
        
        // Check project access for each project
        const accessChecks = await Promise.all(
          projects.map(async (project) => {
            const hasAccess = await hasProjectAccess(project.id);
            return { projectId: project.id, hasAccess };
          })
        );
        
        const accessibleProjectIds = new Set(
          accessChecks
            .filter(check => check.hasAccess)
            .map(check => check.projectId)
        );
        setProjectAccess(accessibleProjectIds);
      } catch (err) {
        console.error('Error loading featured projects:', err);
        setProjectsError('Грешка при зареждане на проектите');
      } finally {
        setProjectsLoading(false);
      }
    };

    loadFeaturedProjects();
  }, []);

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <PageSEO pageKey="home" />
      {/* Header */}
      <Header 
        onLogin={handleLogin}
        onRegister={handleRegister}
        onFindTalent={handleFindTalent}
      />

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
          paddingTop: { xs: 8, md: 20 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '50%',
            height: '100%',
            background: 'linear-gradient(45deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
            borderRadius: '50% 0 0 50%',
            transform: 'translateX(50%)',
          }}
        />
        
        {/* Animated floating shapes */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '10%',
            width: 60,
            height: 60,
            background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
            borderRadius: '50%',
            opacity: 0.3,
            animation: 'float 6s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-20px) rotate(180deg)',
              },
            },
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            right: '15%',
            width: 40,
            height: 40,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #28A0F0 100%)',
            borderRadius: '20%',
            opacity: 0.4,
            animation: 'float 8s ease-in-out infinite reverse',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-30px) rotate(-180deg)',
              },
            },
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            left: '20%',
            width: 80,
            height: 80,
            background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
            borderRadius: '30%',
            opacity: 0.2,
            animation: 'float 10s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-25px) rotate(90deg)',
              },
            },
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            right: '25%',
            width: 50,
            height: 50,
            background: 'linear-gradient(135deg, #10b981 0%, #28A0F0 100%)',
            borderRadius: '50%',
            opacity: 0.3,
            animation: 'float 7s ease-in-out infinite reverse',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-15px) rotate(-90deg)',
              },
            },
          }}
        />
        
        <Box sx={{ width: { xs: '95%', sm: '90%', md: 'calc(80% - 20px)' }, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: { xs: 4, md: 8 } }}>
                <Typography
                  variant={isMobile ? 'h2' : 'h1'}
                  component="h1"
                  sx={{ 
                    fontWeight: 700, 
                    mb: { xs: 4, md: 6 },
                    background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem', lg: '4rem' },
                    lineHeight: 1.1,
                  }}
                >
                  {t('hero.title')}
                </Typography>
                
                <Stack spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 4, md: 8 } }}>
                  {[
                    t('hero.features.marketplace'),
                    t('hero.features.anyJob'),
                    t('hero.features.saveMoney'),
                    t('hero.features.payWhenHappy')
                  ].map((text, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircleIcon sx={{ color: '#28A0F0', fontSize: 20 }} />
                      <Typography variant="body1" sx={{ color: '#a0a0a0' }}>
                        {text}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleFindTalent}
                  sx={{
                    background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #be185d 0%, #9d174d 100%)',
                    }
                  }}
                >
                  {t('hero.buttons.hireFreelancer')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleFindWork}
                  sx={{
                    color: '#ffffff',
                    borderColor: '#2d2d44',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#28A0F0',
                      backgroundColor: 'rgba(40, 160, 240, 0.1)',
                    }
                  }}
                >
                  {t('hero.buttons.earnMoney')}
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', height: { xs: 300, sm: 350, md: 400 } }}>
                {/* Decorative elements */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 200,
                    height: 200,
                    background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                    borderRadius: '50%',
                    opacity: 0.1,
                    animation: 'float 6s ease-in-out infinite',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: 150,
                    height: 150,
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                    borderRadius: '50%',
                    opacity: 0.1,
                    animation: 'float 8s ease-in-out infinite reverse',
                  }}
                />
                
                {/* Main content card */}
                <Card
                  sx={{
                    position: 'relative',
                    zIndex: 2,
                    background: 'rgba(26, 26, 46, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    p: { xs: 3, sm: 4, md: 4 },
                    height: { xs: 'auto', md: '100% + 15px' },
                    minHeight: { xs: 400, sm: 450, md: 'auto' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Stack spacing={{ xs: 2.5, sm: 3, md: 3 }}>
                    {[
                      {
                        title: t('hero.benefits.bestTalent.title'),
                        description: t('hero.benefits.bestTalent.description')
                      },
                      {
                        title: t('hero.benefits.fastBids.title'),
                        description: t('hero.benefits.fastBids.description')
                      },
                      {
                        title: t('hero.benefits.qualityWork.title'),
                        description: t('hero.benefits.qualityWork.description')
                      },
                      {
                        title: t('hero.benefits.beInControl.title'),
                        description: t('hero.benefits.beInControl.description')
                      }
                    ].map((item, index) => (
                      <Box key={index} sx={{ mb: { xs: 2, sm: 2.5, md: 0 } }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600, 
                            mb: { xs: 1, sm: 1.5, md: 1 }, 
                            color: '#28A0F0',
                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                            lineHeight: 1.3
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#a0a0a0',
                            fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.875rem' },
                            lineHeight: { xs: 1.4, sm: 1.5, md: 1.5 }
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Process Section */}
      <Box 
        sx={{ 
          py: { xs: 12, md: 16 }, 
          backgroundColor: '#16162A',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '20%',
            left: '-10%',
            right: '-10%',
            height: '200px',
            background: 'linear-gradient(45deg, #28A0F0 0%, #1d4ed8 50%, #ec4899 100%)',
            opacity: 0.08,
            borderRadius: '50%',
            transform: 'rotate(-15deg) scale(1.2)',
            zIndex: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '10%',
            left: '-15%',
            right: '-15%',
            height: '250px',
            background: 'linear-gradient(135deg, #ec4899 0%, #be185d 50%, #28A0F0 100%)',
            opacity: 0.06,
            borderRadius: '60% 40% 70% 30%',
            transform: 'rotate(25deg) scale(1.1)',
            zIndex: 1,
          }
        }}
      >
        <Box sx={{ width: { xs: '95%', sm: '90%', md: 'calc(85% - 20px)' }, mx: 'auto', px: { xs: 2, sm: 3, md: 6 }, position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
            <Typography 
              variant="h2" 
              component="h2" 
              sx={{ 
                fontWeight: 700, 
                mb: { xs: 3, md: 4 }, 
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem', lg: '2.25rem' },
                color: '#ffffff',
                lineHeight: 1.2
              }}
            >
              {t('process.title')}{' '}
              <Box component="span" sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#28A0F0' }}>
                {t('process.free')}
              </Box>
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#a0a0a0', 
                maxWidth: 800, 
                mx: 'auto', 
                lineHeight: 1.5, 
                fontSize: { xs: '1rem', md: '1.125rem' },
                fontWeight: 400
              }}
            >
              {t('process.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center" sx={{ mb: { xs: 8, md: 12 } }}>
            {processSteps.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    width: 'calc(100% - 20px)',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    backgroundColor: '#1a1a2e',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.4)',
                    },
                  }}
                >
                  {/* Top colored tab */}
                  <Box
                    sx={{
                      background: step.step === 1 
                        ? 'linear-gradient(135deg, #20B2AA 0%, #87CEEB 100%)'
                        : 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '10px solid transparent',
                        borderRight: '10px solid transparent',
                        borderTop: step.step === 1 ? '10px solid #87CEEB' : '10px solid #1d4ed8',
                      }
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: '#ffffff',
                        fontWeight: 700,
                        fontSize: '1.5rem',
                      }}
                    >
                      {step.step}
                    </Typography>
                  </Box>

                  {/* Content section */}
                  <Box sx={{ p: { xs: 3, md: 4 }, height: 'calc(100% - 60px)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    {/* Icon */}
                    <Box sx={{ mb: { xs: 2, md: 3 } }}>
                      {React.cloneElement(step.icon, { 
                        sx: { 
                          fontSize: { xs: 28, md: 32 }, 
                          color: '#28A0F0' 
                        } 
                      })}
                    </Box>

                    {/* Title */}
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        mb: { xs: 2, md: 3 }, 
                        color: '#ffffff',
                        fontSize: { xs: '1rem', md: '1.125rem' },
                        textTransform: 'uppercase',
                        lineHeight: 1.3
                      }}
                    >
                      {step.title}
                    </Typography>

                    {/* Description */}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#a0a0a0', 
                        lineHeight: 1.5,
                        fontSize: '0.9375rem',
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

        </Box>
      </Box>

      {/* Categories Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#0f0f23', position: 'relative', overflow: 'hidden' }}>
        {/* Animated floating shapes */}
        <Box
          sx={{
            position: 'absolute',
            top: '15%',
            left: '5%',
            width: 70,
            height: 70,
            background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
            borderRadius: '50%',
            opacity: 0.15,
            animation: 'float 9s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-25px) rotate(180deg)',
              },
            },
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: '25%',
            right: '8%',
            width: 45,
            height: 45,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #10b981 100%)',
            borderRadius: '25%',
            opacity: 0.2,
            animation: 'float 11s ease-in-out infinite reverse',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-35px) rotate(-180deg)',
              },
            },
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            bottom: '30%',
            left: '12%',
            width: 55,
            height: 55,
            background: 'linear-gradient(135deg, #ec4899 0%, #28A0F0 100%)',
            borderRadius: '40%',
            opacity: 0.18,
            animation: 'float 7s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-20px) rotate(90deg)',
              },
            },
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            bottom: '15%',
            right: '20%',
            width: 35,
            height: 35,
            background: 'linear-gradient(135deg, #10b981 0%, #8b5cf6 100%)',
            borderRadius: '50%',
            opacity: 0.25,
            animation: 'float 8s ease-in-out infinite reverse',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-18px) rotate(-90deg)',
              },
            },
          }}
        />
        
        <Box sx={{ width: { xs: '95%', sm: '90%', md: 'calc(80% - 20px)' }, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 16 } }}>
            <Typography variant="h2" component="h2" sx={{ fontWeight: 700, mb: { xs: 4, md: 6 }, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' } }}>
              {t('categories.title')}
            </Typography>
            <Typography variant="h5" sx={{ color: '#a0a0a0', maxWidth: { xs: '100%', md: 700 }, mx: 'auto', lineHeight: 1.7, fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem', lg: '1.3rem' } }}>
              {t('categories.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
            {categories.map((category, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card
                  sx={{
                    p: { xs: 3, md: 4 },
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    minHeight: { xs: 220, md: 250 },
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                      border: `1px solid ${category.color}40`,
                      '& .category-icon': {
                        transform: 'scale(1.1)',
                      },
                    },
                  }}
                >
                  {/* Icon in top right */}
                  <Box
                    className="category-icon"
                    sx={{
                      position: 'absolute',
                      top: { xs: 16, md: 20 },
                      right: { xs: 16, md: 20 },
                      width: { xs: 40, md: 48 },
                      height: { xs: 40, md: 48 },
                      borderRadius: '50%',
                      backgroundColor: `${category.color}20`,
                      color: category.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {React.cloneElement(category.icon, { sx: { fontSize: { xs: 20, md: 24 } } })}
                  </Box>

                  {/* Content */}
                  <Box sx={{ pr: { xs: 4, md: 6 }, mt: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: { xs: 1.5, md: 2 }, color: '#ffffff', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                      {category.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0', mb: { xs: 3, md: 4 }, lineHeight: 1.6, fontSize: { xs: '0.9rem', md: '0.875rem' } }}>
                      {category.subtitle}
                    </Typography>
                    
                    {/* Bullet Points */}
                    <Box sx={{ mb: 3 }}>
                      {Array.isArray(category.bulletPoints) && category.bulletPoints.map((bulletPoint: string, bulletIndex: number) => (
                        <Box key={bulletIndex} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Box
                            sx={{
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              backgroundColor: category.color,
                              mr: 2,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" sx={{ color: '#d0d0d0', fontSize: '0.9rem' }}>
                            {bulletPoint}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

        </Box>
      </Box>

      {/* Focus Section */}
      <Box sx={{ py: { xs: 16, md: 20 }, backgroundColor: '#16162A' }}>
        <Box sx={{ width: { xs: '95%', sm: '90%', md: 'calc(80% - 20px)' }, mx: 'auto' }}>
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              {/* Visual part with image */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Box
                  component="img"
                  src="/images/focused.png"
                  alt="Focused business strategy"
                  sx={{
                    maxWidth: '100%',
                    height: 'auto',
                    maxHeight: { xs: 300, sm: 400, md: 500, lg: 600 },
                    borderRadius: { xs: 8, md: 12 },
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    }
                  }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h2" 
                component="h2" 
                sx={{ 
                  fontWeight: 700, 
                  mb: { xs: 3, md: 4 }, 
                  color: '#ffffff',
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem', lg: '2.25rem' },
                  lineHeight: 1.2
                }}
              >
                {t('focusSection.title')}
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#ffffff', 
                  mb: { xs: 2, md: 3 }, 
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', md: '1.1rem' }
                }}
              >
                {t('focusSection.description1')}
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#ffffff', 
                  mb: { xs: 3, md: 4 }, 
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', md: '1.1rem' }
                }}
              >
                {t('focusSection.description2')}{' '}
                <Box 
                  component="span" 
                  sx={{ 
                    color: '#28A0F0', 
                    fontWeight: 600, 
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {t('focusSection.advertisingLink')}
                </Box>
                .
              </Typography>
              
            </Grid>
            </Grid>
        </Box>
      </Box>

      {/* Featured Projects Section */}
                <Box
                  sx={{
          py: { xs: 16, md: 20 }, 
          backgroundColor: '#0a0a1a',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ width: { xs: '95%', sm: '90%', md: 'calc(85% - 20px)' }, mx: 'auto' }}>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              textAlign: 'center', 
              mb: 8, 
              fontWeight: 700, 
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              color: '#ffffff'
            }}
          >
            {t('projects.title')}
          </Typography>
          
          {projectsError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {projectsError}
            </Alert>
          )}

          {projectsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#28A0F0' }} />
            </Box>
          ) : featuredProjects.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" sx={{ color: '#a0a0a0', mb: 2 }}>
                Няма налични проекти в момента
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Проверете отново по-късно за нови проекти
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxWidth: { xs: '100%', md: 1400 }, mx: 'auto', display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 } }}>
              {featuredProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  title={project.title}
                  mainCategory={project.category?.name || t('common.other')}
                  projectDuration="30 days"
                  budget={project.budget_min && project.budget_max 
                    ? `€${project.budget_min.toLocaleString()} - €${project.budget_max.toLocaleString()}`
                    : project.budget_min 
                    ? `${t('common.from')} €${project.budget_min.toLocaleString()}`
                    : t('common.negotiable')
                  }
                  client={projectAccess.has(project.id) 
                    ? (project.client.full_name || project.client.company || t('findWork.anonymous'))
                    : t('findWork.anonymous')
                  }
                  currentStatus={t('projectDetails.active')}
                  startDeadline={project.deadline ? new Date(project.deadline).toLocaleDateString('bg-BG') : t('projectDetails.flexible')}
                  budgetType="FIXED"
                  preferredContractorType="FREELANCER"
                  description={projectAccess.has(project.id) 
                    ? project.description
                    : project.description.length > 100 
                      ? `${project.description.substring(0, 100)}...`
                      : project.description
                  }
                  onViewFull={() => {
                    navigate(`/project/${project.id}`);
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
        
        {/* Animated floating shapes */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: '5%',
            width: 70,
            height: 70,
                    background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
            borderRadius: '50%',
            opacity: 0.15,
            animation: 'float 9s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-25px) rotate(180deg)',
              },
            },
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: '60%',
            right: '8%',
            width: 50,
            height: 50,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #28A0F0 100%)',
            borderRadius: '30%',
            opacity: 0.2,
            animation: 'float 11s ease-in-out infinite reverse',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-30px) rotate(-180deg)',
              },
            },
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            bottom: '15%',
            left: '12%',
            width: 60,
            height: 60,
            background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
            borderRadius: '40%',
            opacity: 0.1,
            animation: 'float 13s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-35px) rotate(180deg)',
              },
            },
          }}
        />
      </Box>

      {/* Testimonials Section */}
                <Box
                  sx={{
          py: { xs: 16, md: 20 }, 
          backgroundColor: '#0f0f23',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ width: { xs: '95%', sm: '90%', md: 'calc(85% - 20px)' }, mx: 'auto' }}>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              textAlign: 'center', 
              mb: 8, 
              fontWeight: 700, 
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              color: '#ffffff'
            }}
          >
            {t('testimonials.title')}
          </Typography>
          
          <Box sx={{ position: 'relative', maxWidth: { xs: '100%', md: 1400 }, mx: 'auto' }}>
            {/* Testimonial Cards */}
            <Box 
              className={`drag-container ${isDragging ? 'dragging' : ''}`}
              sx={{ 
                    display: 'flex',
                overflow: 'hidden', 
                borderRadius: 3,
                userSelect: 'none'
              }}
              onMouseDown={handleDragStart}
              onMouseMove={handleDragMove}
              onMouseUp={handleDragEnd}
              onMouseLeave={handleDragEnd}
              onTouchStart={handleDragStart}
              onTouchMove={handleDragMove}
              onTouchEnd={handleDragEnd}
            >
              {(t('testimonials.testimonials', { returnObjects: true }) as any[]).map((testimonial: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      minWidth: '100%',
                      display: 'flex',
                    justifyContent: 'center',
                      transform: `translateX(calc(-${currentTestimonial * 100}% + ${isDragging ? (dragCurrent - dragStart) * 0.1 : 0}px))`,
                      transition: isDragging ? 'none' : 'transform 0.5s ease-in-out',
                    }}
                  >
                  <Card
                    sx={{
                      width: { xs: '100%', sm: '95%', md: '90%' },
                      maxWidth: { xs: '100%', md: 1000 },
                      background: index % 2 === 0 
                        ? 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)'
                        : 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                      borderRadius: { xs: 3, md: 4 },
                      p: { xs: 3, sm: 4, md: 6 },
                    position: 'relative',
                      minHeight: { xs: 300, sm: 350, md: 400 },
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      my: { xs: 2, md: 4 },
                      transition: 'all 0.3s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-8px) scale(1.02)',
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                        '& .quote-icon': {
                          opacity: 0.5,
                          transform: 'scale(1.1)',
                        }
                      }
                    }}
                  >
                    {/* Quote icons */}
                    <QuoteIcon 
                      className="quote-icon"
                      sx={{ 
                      position: 'absolute',
                        top: { xs: 16, md: 24 }, 
                        right: { xs: 16, md: 24 }, 
                        fontSize: { xs: 32, sm: 48, md: 64 }, 
                        color: '#ffffff', 
                      opacity: 0.3,
                        transition: 'all 0.3s ease-in-out'
                      }} 
                    />
                    <QuoteIcon 
                      className="quote-icon"
                      sx={{ 
                        position: 'absolute', 
                        bottom: { xs: 16, md: 24 }, 
                        left: { xs: 16, md: 24 }, 
                        fontSize: { xs: 32, sm: 48, md: 64 }, 
                        color: '#ffffff', 
                        opacity: 0.3,
                        transform: 'rotate(180deg)',
                        transition: 'all 0.3s ease-in-out'
                      }} 
                    />
                    
                    <Box>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          color: '#ffffff', 
                          fontWeight: 700, 
                          mb: { xs: 1.5, md: 2 },
                          fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
                        }}
                      >
                        {testimonial.name}
                      </Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#ffffff', 
                          opacity: 0.9, 
                          mb: { xs: 3, md: 4 },
                          fontSize: { xs: '0.9rem', sm: '1rem', md: '1.125rem' }
                        }}
                      >
                        {testimonial.position}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: '#ffffff', 
                          lineHeight: 1.7,
                          fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
                        }}
                      >
                        {testimonial.text}
                      </Typography>
                </Box>
                  </Card>
              </Box>
              ))}
            </Box>

            {/* Navigation arrows */}
            <IconButton
              onClick={goToPrevious}
              sx={{
                position: 'absolute',
                left: { xs: -40, md: -60 },
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
                display: { xs: 'none', sm: 'flex' }
              }}
            >
              <ChevronLeftIcon />
            </IconButton>

            <IconButton
              onClick={goToNext}
              sx={{
                position: 'absolute',
                right: { xs: -40, md: -60 },
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
                display: { xs: 'none', sm: 'flex' }
              }}
            >
              <ChevronRightIcon />
            </IconButton>

            {/* Dots indicator */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 3, md: 4 }, gap: 1 }}>
              {(t('testimonials.testimonials', { returnObjects: true }) as any[]).map((_: any, index: number) => (
                <Box
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: index === currentTestimonial ? '#28A0F0' : 'rgba(255, 255, 255, 0.3)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: index === currentTestimonial ? '#28A0F0' : 'rgba(255, 255, 255, 0.5)',
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
        
        {/* Animated floating shapes */}
        <Box
          sx={{
            position: 'absolute',
            top: '15%',
            left: '8%',
            width: 80,
            height: 80,
            background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
            borderRadius: '50%',
            opacity: 0.2,
            animation: 'float 8s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-30px) rotate(180deg)',
              },
            },
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: '25%',
            right: '12%',
            width: 60,
            height: 60,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #28A0F0 100%)',
            borderRadius: '30%',
            opacity: 0.25,
            animation: 'float 10s ease-in-out infinite reverse',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-25px) rotate(-180deg)',
              },
            },
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            left: '15%',
            width: 70,
            height: 70,
            background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
            borderRadius: '40%',
            opacity: 0.15,
            animation: 'float 12s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-35px) rotate(180deg)',
              },
            },
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            bottom: '30%',
            right: '8%',
            width: 50,
            height: 50,
            background: 'linear-gradient(135deg, #1d4ed8 0%, #8b5cf6 100%)',
            borderRadius: '20%',
            opacity: 0.3,
            animation: 'float 9s ease-in-out infinite reverse',
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) rotate(0deg)',
              },
              '50%': {
                transform: 'translateY(-20px) rotate(-180deg)',
              },
            },
          }}
        />
      </Box>

      <Footer />

    </Box>
  );
}

